import React from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ContactMailOutlinedIcon from '@mui/icons-material/ContactMailOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { CollectionSiteSchema } from '../../types/collection-site.type';

export interface BranchDetailsCardProps {
  site: CollectionSiteSchema | null;
}

function getOperatingHoursText(site: CollectionSiteSchema): string {
  const raw = site.data?.operating_hours;
  return typeof raw === 'string' ? raw.trim() : '';
}

function getSchedule(site: CollectionSiteSchema): { days: string[]; pickupTime: string } {
  const s = site.data?.schedule;
  if (!s || typeof s !== 'object') {
    return { days: [], pickupTime: '' };
  }
  const rawDays = s.pickup_days;
  const days = Array.isArray(rawDays)
    ? rawDays
        .filter((d): d is string => typeof d === 'string' && d.trim().length > 0)
        .map((d) => d.trim())
    : [];
  const pickupTime = typeof s.pickup_time === 'string' ? s.pickup_time.trim() : '';
  return { days, pickupTime };
}

function hasOperatingHoursContent(site: CollectionSiteSchema): boolean {
  const text = getOperatingHoursText(site);
  const { days, pickupTime } = getSchedule(site);
  return Boolean(text || days.length > 0 || pickupTime);
}

function hasContactInfo(site: CollectionSiteSchema): boolean {
  const c = site.data?.contact_info;
  if (!c) return false;
  return Boolean(
    (typeof c.name === 'string' && c.name.trim()) ||
      (typeof c.phone === 'string' && c.phone.trim()) ||
      (typeof c.email === 'string' && c.email.trim())
  );
}

const chipSx = {
  height: 22,
  fontSize: '0.7rem',
  '& .MuiChip-label': { px: 0.75, py: 0 },
} as const;

const EmptyBlock: React.FC<{ icon: React.ReactNode; message: string }> = ({ icon, message }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      py: 0.75,
      px: 0.5,
      gap: 0.75,
    }}
  >
    <Box sx={{ color: 'action.disabled', display: 'flex', '& .MuiSvgIcon-root': { fontSize: 28 } }}>
      {icon}
    </Box>
    <Typography variant="caption" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

/** Operating hours, pickup schedule (chips), and contact — compact height. */
const BranchDetailsCard: React.FC<BranchDetailsCardProps> = ({ site }) => {
  if (!site) {
    return (
      <Typography variant="caption" color="text.secondary">
        Select a branch to see operating hours and contact.
      </Typography>
    );
  }

  const hoursText = getOperatingHoursText(site);
  const { days, pickupTime } = getSchedule(site);
  const contact = site.data?.contact_info;
  const name = typeof contact?.name === 'string' ? contact.name.trim() : '';
  const email = typeof contact?.email === 'string' ? contact.email.trim() : '';
  const phone = typeof contact?.phone === 'string' ? contact.phone.trim() : '';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 1.5, md: 2 },
        alignItems: { xs: 'stretch', md: 'center' },
        width: '100%',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.35, lineHeight: 1.2 }}>
          Operating hours
        </Typography>
        {hasOperatingHoursContent(site) ? (
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
            <ScheduleOutlinedIcon sx={{ color: 'primary.main', fontSize: 18, flexShrink: 0 }} />
            <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
              {hoursText ? (
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ lineHeight: 1.35, fontSize: '0.8125rem' }}
                  noWrap
                  title={hoursText}
                >
                  {hoursText}
                </Typography>
              ) : null}
              {(days.length > 0 || pickupTime) ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {days.map((day, index) => (
                    <Chip
                      key={`${day}-${index}`}
                      label={day}
                      size="small"
                      variant="outlined"
                      sx={chipSx}
                    />
                  ))}
                  {pickupTime ? (
                    <Chip
                      icon={<AccessTimeOutlinedIcon sx={{ fontSize: '0.95rem !important' }} />}
                      label={pickupTime}
                      size="small"
                      variant="outlined"
                      sx={chipSx}
                    />
                  ) : null}
                </Box>
              ) : null}
            </Stack>
          </Box>
        ) : (
          <EmptyBlock
            icon={<ScheduleOutlinedIcon />}
            message="No operating hours or pickup schedule have been set for this site."
          />
        )}
      </Box>

      <Divider
        orientation="vertical"
        flexItem
        sx={{ display: { xs: 'none', md: 'block' }, borderColor: 'divider', alignSelf: 'stretch' }}
      />
      <Divider sx={{ display: { xs: 'block', md: 'none' }, borderColor: 'divider', width: '100%' }} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.35, lineHeight: 1.2 }}>
          Contact
        </Typography>
        {hasContactInfo(site) ? (
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
            <ContactMailOutlinedIcon sx={{ color: 'primary.main', fontSize: 18, flexShrink: 0 }} />
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
              }}
            >
              {name ? (
                <Chip label={name} size="small" variant="outlined" sx={{ ...chipSx, maxWidth: '100%' }} />
              ) : null}
              {email ? (
                <Chip
                  label={email}
                  size="small"
                  variant="outlined"
                  sx={{ ...chipSx, maxWidth: '100%' }}
                  title={email}
                />
              ) : null}
              {phone ? (
                <Chip label={phone} size="small" variant="outlined" sx={{ ...chipSx, maxWidth: '100%' }} />
              ) : null}
            </Box>
          </Box>
        ) : (
          <EmptyBlock
            icon={<ContactMailOutlinedIcon />}
            message="No contact information has been set for this site."
          />
        )}
      </Box>
    </Box>
  );
};

export default BranchDetailsCard;
